import type { DashboardSpec, NotAppliedItem } from './dashboard.types';

export interface AppError {
    code: string;
    title: string;
    message?: string;
    description?: string;
    status?: number;
    originalError?: unknown;
}
  
export interface StreamErrorResponse {
    type: 'error';
    error_code: string;
    status_code: number;
    title: string;
    content: string;
    request_id?: string;
    timestamp?: string;
    source?: string;
}
  
export interface RoleAccessError {
    code: string;
    title: string;
    description: string;
    status?: number;
    originalError?: unknown;
}

/**
 * Single source of truth for the bare-TypeError messages browsers throw when
 * fetch() fails due to a network disconnect (WiFi blip, VPN reconnect, etc.):
 *   - Chrome / Edge: "TypeError: Failed to fetch"
 *   - Safari / iOS: "TypeError: network error"
 * Consumed by Sentry's ignoreErrors filter (to drop the noise at the SDK
 * boundary) and by chatApi's isNetworkDisconnect() helper (to convert these
 * into a typed NetworkDisconnectError). Keep them co-located with the error
 * class so a future browser-quirk fix updates both paths in one place.
 */
export const NETWORK_DISCONNECT_PATTERNS = [
  /Failed to fetch/i,
  /^network error$/i,
] as const;

/**
 * A non-OK HTTP response from a fetch-based SSE stream (e.g.
 * `POST /dashboards/queries/execute`), carrying the response `status` alongside
 * the human-readable message parsed from the error body. A bare `Error` loses the
 * status, so callers can't distinguish "you no longer have access" (403 — retrying
 * is pointless) from a transient transport failure (500 — Retry is meaningful).
 */
export class StreamHttpError extends Error {
  readonly status: number;
  /** Backend `error_code` from the response body, when it sent one. */
  readonly code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'StreamHttpError';
    this.status = status;
    this.code = code;
  }
}

/**
 * An `error` event the dashboard agent sent inside a 2xx stream: the server ran the
 * turn and reported that it failed (compile failure, rejected patch, agent
 * exception or server-side timeout). The backend keeps its older saved spec, so a
 * draft already on screen is still what Publish saves. Client-side failures
 * (timeout, network drop, abort) never use this class: the server may still finish
 * and save that turn.
 */
export class DashboardAgentError extends Error {
  /** The backend `error_code` (e.g. `DASHBOARD_SPEC_COMPILE_FAILED`), when sent. */
  readonly code: string | undefined;
  /** The draft the server kept after this failure (its last saved spec), when sent. */
  readonly spec: DashboardSpec | undefined;
  /** A rejected change: the user's change in their words, when sent. */
  readonly request: string | undefined;
  /**
   * The turn's unmet asks when the event carried `not_applied` (the "Not applied:"
   * text block is then already stripped from `message`); `undefined` when it did not.
   */
  readonly notApplied: NotAppliedItem[] | undefined;

  constructor(
    message: string,
    code?: string,
    spec?: DashboardSpec,
    details: { request?: string; notApplied?: NotAppliedItem[] } = {},
  ) {
    super(message);
    this.name = 'DashboardAgentError';
    this.code = code;
    this.spec = spec;
    this.request = details.request;
    this.notApplied = details.notApplied;
  }
}

export class NetworkDisconnectError extends Error {
  readonly isNetworkDisconnect = true as const;
  readonly originalError: unknown;

  constructor(cause?: unknown) {
    super('Network connection lost during stream');
    this.name = 'NetworkDisconnectError';
    this.originalError = cause;
  }
}

/** One document-level reason the backend attached to a rejection (`issues[]`). */
export interface ApiErrorIssue {
    reason_code?: string;
    message: string;
}

/**
 * The backend's standard error envelope for REST endpoints
 * (`app.errors.handlers.create_error_response`). Every field is optional here
 * because the body is untrusted wire data — {@link parseApiErrorEnvelope}
 * keeps only fields of the right type. `content` is the user-facing sentence;
 * `request_id` correlates a report with server logs. (The envelope also carries
 * `type`, `status_code`, `title`, `timestamp`, `source` — not read by any caller.)
 */
export interface ApiErrorEnvelope {
    error_code?: string;
    content?: string;
    request_id?: string;
    issues: ApiErrorIssue[];
}
